/**
 * AggregateRating — small visual rating block for V3 sections.
 *
 * Matches the JSON-LD `aggregateRating` emitted by the host page so the
 * rendered DOM and the structured data tell the same story (Google's
 * structured-data policy requires this).
 *
 * Pure presentational: the host page is responsible for the matching
 * `<script type="application/ld+json">` block.
 */

import React from 'react';

type Props = {
  /** 0–5, one decimal. */
  value: number;
  /** Total number of reviews aggregated. */
  count: number;
  /** Localized "4.9 out of 5 stars" string for screen readers. */
  starsAriaLabel: string;
  /** Localized "based on {count} reviews" suffix. */
  basedOnLabel: string;
  /** Optional accent color for the filled stars. Defaults to V3 cyan. */
  accent?: string;
};

const STAR_FILLED = (color: string, key: number) => (
  <svg key={key} width={18} height={18} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill={color} d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const STAR_HALF = (color: string, key: number) => (
  <svg key={key} width={18} height={18} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <defs>
      <linearGradient id={`half-${key}`}>
        <stop offset="50%" stopColor={color} />
        <stop offset="50%" stopColor="rgba(255,255,255,0.18)" />
      </linearGradient>
    </defs>
    <path
      fill={`url(#half-${key})`}
      d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </svg>
);

const STAR_EMPTY = (key: number) => (
  <svg key={key} width={18} height={18} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path
      fill="rgba(255,255,255,0.18)"
      d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </svg>
);

const AggregateRating = ({ value, count, starsAriaLabel, basedOnLabel, accent = '#22D3EE' }: Props) => {
  const full = Math.floor(value);
  const hasHalf = value - full >= 0.25 && value - full < 0.75;
  const filledForRender = hasHalf ? full : Math.round(value);

  const stars: React.ReactNode[] = [];
  for (let i = 0; i < 5; i++) {
    if (i < filledForRender) stars.push(STAR_FILLED(accent, i));
    else if (i === filledForRender && hasHalf) stars.push(STAR_HALF(accent, i));
    else stars.push(STAR_EMPTY(i));
  }

  const formatted = value.toFixed(1);

  return (
    <div
      className="inline-flex items-center"
      style={{ gap: 10, margin: '0 0 28px', flexWrap: 'wrap' }}
      role="img"
      aria-label={starsAriaLabel}
    >
      <div className="inline-flex items-center" style={{ gap: 2 }} aria-hidden="true">
        {stars}
      </div>
      <span style={{ color: '#F5F7FA', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }} aria-hidden="true">
        {formatted}/5
      </span>
      <span style={{ color: '#B4C1D8', fontSize: 14 }}>{basedOnLabel}</span>
    </div>
  );
};

export default AggregateRating;
